import React from 'react';
import { DateInputRu } from './DateInputRu';
import { mskToday, mskAddDays } from '../../utils/time';
import './DateInput.css';

/**
 * DateInput — поле даты ДД.ММ.ГГГГ (на основе DateInputRu). Значение — ISO
 * «ГГГГ-ММ-ДД», как у <input type="date">: onChange(iso) — '' если очищено.
 *
 *   value, onChange, min, max, disabled, size ('sm'|'md'|'lg'), fullWidth,
 *   invalid, aria-label, title
 *
 * Заменяет 62 браузерных <input type="date"> (они показывали mm/dd/yyyy на
 * английской Windows и белую иконку в тёмной теме) — при миграции страниц.
 */
export function DateInput({ size = 'md', fullWidth = false, invalid = false, className = '', ...props }) {
  const cls = [
    size !== 'md' && `date-ru--${size}`,
    fullWidth && 'date-ru--block',
    invalid && 'date-ru--invalid',
    className,
  ].filter(Boolean).join(' ');
  return <DateInputRu className={cls} {...props} />;
}

const monthStart = (key) => `${key.slice(0, 7)}-01`;

/** Пресеты периода — по Москве (utils/time). */
export const PERIOD_PRESETS = [
  { key: 'today', label: 'Сегодня', range: () => { const t = mskToday(); return { from: t, to: t }; } },
  { key: 'yesterday', label: 'Вчера', range: () => { const y = mskAddDays(mskToday(), -1); return { from: y, to: y }; } },
  { key: '7d', label: '7 дней', range: () => { const t = mskToday(); return { from: mskAddDays(t, -6), to: t }; } },
  { key: '30d', label: '30 дней', range: () => { const t = mskToday(); return { from: mskAddDays(t, -29), to: t }; } },
  { key: 'month', label: 'Этот месяц', range: () => { const t = mskToday(); return { from: monthStart(t), to: t }; } },
  { key: 'prev_month', label: 'Прошлый месяц', range: () => {
    const prevEnd = mskAddDays(monthStart(mskToday()), -1);
    return { from: monthStart(prevEnd), to: prevEnd };
  } },
];

/**
 * PeriodPicker — период «с … по …» + пресеты.
 *
 *   from, to  — ISO «ГГГГ-ММ-ДД» ('' — не задано)
 *   onChange({ from, to })
 *   presets   — ключи из PERIOD_PRESETS (по умолчанию today/7d/30d/month),
 *               false — без пресетов
 *   min, max, size, disabled
 *
 * «с» не может быть позже «по»: календарь ограничен, при наборе руками
 * DateInputRu не примет дату вне диапазона.
 */
export function PeriodPicker({
  from = '',
  to = '',
  onChange,
  presets = ['today', '7d', '30d', 'month'],
  min,
  max,
  size = 'md',
  disabled = false,
  className = '',
}) {
  const list = presets ? PERIOD_PRESETS.filter(p => presets.includes(p.key)) : [];
  const activeKey = list.find(p => { const r = p.range(); return r.from === from && r.to === to; })?.key;
  const set = (patch) => onChange && onChange({ from, to, ...patch });
  return (
    <div className={`ui-period ${className}`} role="group" aria-label="Период">
      <span className="ui-period__fields">
        <span className="ui-period__lbl">с</span>
        <DateInput value={from} onChange={v => set({ from: v })} min={min} max={to || max}
                   size={size} disabled={disabled} aria-label="Период с" />
        <span className="ui-period__lbl">по</span>
        <DateInput value={to} onChange={v => set({ to: v })} min={from || min} max={max}
                   size={size} disabled={disabled} aria-label="Период по" />
      </span>
      {list.length > 0 && (
        <span className="ui-period__presets">
          {list.map(p => (
            <button
              key={p.key}
              type="button"
              disabled={disabled}
              className={`ui-period__preset ${activeKey === p.key ? 'is-active' : ''}`}
              aria-pressed={activeKey === p.key}
              onClick={() => onChange && onChange(p.range())}
            >
              {p.label}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}
